import type { InstallationResolveSpecialFee } from "../types/installation";
import {
  findCatalogRowById,
  readResolveRequired,
  readResolveTaxable,
  resolveSpecialFeeTaxableForPayload,
} from "./readResolveTaxable";

export type InstallationUiSpecialFee = {
  id?: string;
  name?: string;
  amount?: number;
  taxable?: boolean;
  required?: boolean;
  tax_percentage?: number | null;
  tax_amount?: number | null;
  specialfee_id?: string;
  specialfee_name?: string;
  specialfee_amount?: number;
  specialfee_taxable?: boolean;
  is_taxable?: boolean;
};

export function installationFeeRequired(
  f: { required?: boolean } | null | undefined
): boolean {
  return f?.required === true;
}

export function installationFeeTaxable(
  f: InstallationUiSpecialFee | Record<string, unknown> | null | undefined,
  catalog?: unknown[] | null
): boolean {
  return resolveSpecialFeeTaxableForPayload(
    f as Record<string, unknown> | null | undefined,
    catalog
  );
}

/** Normalize selected fee state for resolve/submit (preserves catalog taxable flags). */
export function normalizeInstallationSelectedFee(
  f: Record<string, unknown>,
  catalog?: unknown[] | null
): InstallationUiSpecialFee & {
  id: string;
  name: string;
  amount: number;
  taxable: boolean;
  tax_percentage: number | null;
  tax_amount: number;
  tax_rule_id: string | null;
  tax_group_id: string | null;
  required: boolean;
} {
  const id = String(f?.id ?? f?.specialfee_id ?? "");
  const catalogRow = findCatalogRowById(catalog, id);
  const taxPctRaw = f?.tax_percentage ?? f?.specialfee_tax_percentage ?? catalogRow?.tax_percentage;
  return {
    id,
    name: String(f?.name ?? f?.specialfee_name ?? catalogRow?.name ?? "Special Fee"),
    amount: Number(f?.amount ?? f?.specialfee_amount ?? catalogRow?.amount ?? 0),
    taxable: installationFeeTaxable(f, catalog),
    tax_percentage: Number.isFinite(Number(taxPctRaw)) ? Number(taxPctRaw) : null,
    tax_amount: Number(f?.tax_amount ?? f?.specialfee_tax_amount ?? 0),
    tax_rule_id: f?.tax_rule_id != null ? String(f.tax_rule_id) : catalogRow?.tax_rule_id != null ? String(catalogRow.tax_rule_id) : null,
    tax_group_id: f?.tax_group_id != null ? String(f.tax_group_id) : catalogRow?.tax_group_id != null ? String(catalogRow.tax_group_id) : null,
    required:
      installationFeeRequired(f as InstallationUiSpecialFee) ||
      readResolveRequired(catalogRow),
  };
}

export function mapInstallationSpecialFeeForResolve(
  f: InstallationUiSpecialFee,
  catalog?: unknown[] | null
): InstallationResolveSpecialFee {
  const taxable = installationFeeTaxable(f, catalog);
  return {
    specialfee_id: String(f.id ?? f.specialfee_id ?? ""),
    specialfee_name: String(f.name ?? f.specialfee_name ?? ""),
    specialfee_amount: Number(f.amount ?? f.specialfee_amount ?? 0),
    is_taxable: taxable,
    itemType: "SpecialFee",
    required: installationFeeRequired(f),
  };
}

export function mapInstallationSpecialFeeForSubmit(
  f: InstallationUiSpecialFee,
  options?: { zeroOutTaxFields?: boolean; catalog?: unknown[] | null }
) {
  const zero = options?.zeroOutTaxFields === true;
  const taxable = installationFeeTaxable(f, options?.catalog);
  return {
    specialfee_id: String(f.id ?? f.specialfee_id ?? ""),
    specialfee_name: String(f.name ?? f.specialfee_name ?? ""),
    specialfee_amount: Number(f.amount ?? f.specialfee_amount ?? 0),
    specialfee_tax_percentage: zero
      ? 0
      : taxable === false
        ? 0
        : Number.isFinite(Number(f.tax_percentage))
          ? Number(f.tax_percentage)
          : 0,
    specialfee_tax_amount: zero ? 0 : taxable === false ? 0 : Number(f.tax_amount || 0),
    specialfee_taxable: taxable,
    required: installationFeeRequired(f),
  };
}

export function mapSavedInstallationSpecialFeeToUi(f: Record<string, unknown>) {
  return {
    id: String(f?.specialfee_id ?? f?.id ?? ""),
    name: String(f?.specialfee_name ?? f?.name ?? "Special Fee"),
    amount: Number(f?.specialfee_amount ?? f?.amount ?? 0),
    taxable: readResolveTaxable(f, ["specialfee_taxable", "taxable", "is_taxable"], true),
    tax_percentage: Number.isFinite(Number(f?.specialfee_tax_percentage ?? f?.tax_percentage))
      ? Number(f?.specialfee_tax_percentage ?? f?.tax_percentage)
      : null,
    tax_amount: Number(f?.specialfee_tax_amount ?? f?.tax_amount ?? 0),
    tax_rule_id: f?.tax_rule_id ?? null,
    tax_group_id: f?.tax_group_id ?? null,
    required: f?.required === true,
  };
}
